import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TailSpin } from 'react-loader-spinner';
import SftGlobalData from '../library/SftGlobalData';
import Colors from '../library/Colors';
import {
    CLASSIC_TOKENS, SAMPLE_VALUES, BAR_TYPES,
    emptyModel, newElement, compile, parse, substituteSample, verifyRoundTrip,
} from '../library/labelDesigner/zplModel';
import { importFr3, decodeFr3Base64 } from '../library/labelDesigner/fr3Import';
import { openFr3, applyFr3Edits, serializeFr3, encodeBase64, setFr3Script, setFr3Page, getFr3Page } from '../library/labelDesigner/fr3Doc';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Menu from '@mui/material/Menu';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import IconButton from '@mui/material/IconButton';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import BorderLeftIcon from '@mui/icons-material/BorderLeft';
import BorderRightIcon from '@mui/icons-material/BorderRight';
import BorderTopIcon from '@mui/icons-material/BorderTop';
import BorderBottomIcon from '@mui/icons-material/BorderBottom';
import AlignHorizontalLeftIcon from '@mui/icons-material/AlignHorizontalLeft';
import AlignHorizontalCenterIcon from '@mui/icons-material/AlignHorizontalCenter';
import AlignHorizontalRightIcon from '@mui/icons-material/AlignHorizontalRight';
import AlignVerticalTopIcon from '@mui/icons-material/AlignVerticalTop';
import AlignVerticalCenterIcon from '@mui/icons-material/AlignVerticalCenter';
import AlignVerticalBottomIcon from '@mui/icons-material/AlignVerticalBottom';
import HeightIcon from '@mui/icons-material/Height';
import EastIcon from '@mui/icons-material/East';
import SouthIcon from '@mui/icons-material/South';

// Standard Windows fonts — the till machines run Windows, so any of these is
// safe for FastReport. An unknown Font.Name from the file is prepended so it
// never gets silently replaced.
const FR3_FONTS = [
    'Arial', 'Arial Narrow', 'Calibri', 'Courier New', 'Georgia',
    'Segoe UI', 'Tahoma', 'Times New Roman', 'Trebuchet MS', 'Verdana',
];

// PrintQ columns actually used across the client label templates (survey of
// C:\SoftTouch\reports Etiket*/cmbra* .fr3 files) — the practical field set
// for adding data memos to an .fr3 label.
const FR3_LABEL_FIELDS = [
    'barcode', 'vp', 'wsp', 'naam', 'levNaam', 'levart1', 'levart2',
    'kleurlev', 'kleuroms', 'seizoen', 'codeoms', 'mtomsX', 'mtomsY',
    'c1oms', 'c2oms', 'c3oms', 'size',
];

// Sample values for the fr3 preview, keyed by the PrintQ column name. Same
// idea as the ZPL SAMPLE_VALUES: show what the form will look like FILLED,
// not the raw [DM1.PrintQ."x"] plumbing. Unknown fields fall back to a
// name-based guess (see fr3Sample).
const FR3_SAMPLE_VALUES = {
    // article / label
    barcode: '5400000123457', vp: '69,95', wsp: '34,50', naam: 'Jansens',
    naam2: 'Bouwmaterialen', levnaam: 'STATE OF ART', levart1: '622 13965',
    levart2: 'L34', kleurlev: '9992', kleuroms: 'Marineblauw', seizoen: 'W26',
    codeoms: 'Dames Truien', mtomsx: 'M', mtomsy: '34', size: 'M',
    c1oms: 'Dames', c2oms: 'Truien', c3oms: 'Wol', oms: 'Broek State of art',
    oms1: 'Broek State of art', oms2: 'Wol - Navy',
    // customer / document
    klnr: '10248', firstname: 'Marie', adres: 'Kerkstraat 12', postcode: '2000',
    gemeente: 'Antwerpen', land: 'BE', gsm: '0470 12 34 56',
    btwnr: 'BE 0123.456.789', docnr: '2026/00147', docdatum: '26/08/2026',
    deldate: '28/08/2026', dag: '26/08/2026',
    // corporation
    corpname: 'SoftTouch bvba', corpaddress1: 'Industrieweg 8', corpzip: '2500',
    corpcity: 'Lier', corptva: 'BE 0987.654.321',
    // amounts
    aantal: '2', ep: '69,95', korting: '6,99', btw: '21',
    t_netto: '132,91', t_btw: '27,91', t_afronding: '0,00', totaal: '160,82',
    t_betaald: '160,82', t_factuur: '160,82', betaal_methode: 'Bancontact',
    free1: '', free2: '',
};

// PrintQ columns the client invoice forms actually use (survey of the A4
// invoice templates): customer block, document header, corporation block,
// line columns and the totals.
const FR3_INVOICE_FIELDS = [
    'docnr', 'docdatum', 'deldate', 'naam', 'naam2', 'adres', 'postcode',
    'gemeente', 'land', 'btwnr', 'corpName', 'corpAddress1', 'corpZip',
    'corpCity', 'corpTva', 'oms', 'aantal', 'ep', 'korting', 'btw',
    't_netto', 't_btw', 't_afronding', 'totaal', 't_betaald', 't_factuur',
    'betaal_methode', 'free1', 'free2',
];

/**
 * LabelDesignerScreen — FastReports-style online editor for the ZPL label
 * layout (kasvar mobileZPLcode.value_blob). Design artifact §5.
 *
 * The element model is the source of truth; ZPL is compiled output. A kasvar
 * without a stored designer model goes through the ZPL subset parser; if that
 * fails the screen opens in raw-ZPL mode instead of mangling the layout.
 *
 * Scope-aware: the layout can be defined per Global / store / register (the
 * kasvar location cascade — the worker resolves value_blob per register, so
 * an override wins automatically on the devices).
 *
 * Interaction: drag (0.5 mm grid + alignment guides), Ctrl-click multi-select
 * (group move), Del removes, arrows move, Shift+arrow resizes.
 *
 * Devices pick a save up on their next prog-var sync — no app deploy.
 */
const LabelDesignerScreen = (props) => {
    const navigate = useNavigate();
    // fr3 document mode (phase 6): /mobile/designer/:src/:name opens an
    // .fr3 — src 'labels' = full editing (delete allowed), src 'reports' =
    // invoice TOUCH-UP (structure preserved, no delete; also reached from a
    // repid assignment — its report_.repform layout is a CUSTOM report in
    // the client dir). No params = the classic ZPL kasvar document.
    const params = useParams();
    const fr3Source = params.src === 'labels' || params.src === 'reports' ? params.src : null;
    const fr3Name = fr3Source && params.name ? decodeURIComponent(params.name) : null;
    const fr3Mode = fr3Name !== null;
    const fr3Invoice = fr3Source === 'reports';
    const fr3Ref = useRef(null); // { handle, originalXml, lanes }
    const [lanes, setLanes] = useState([]);

    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [model, setModel] = useState(emptyModel());
    const [selectedIds, setSelectedIds] = useState([]);
    const [rawMode, setRawMode] = useState(false);
    const [rawZpl, setRawZpl] = useState('');
    const [rawReason, setRawReason] = useState('');
    const [sampleOn, setSampleOn] = useState(true);
    const [labelList, setLabelList] = useState([]);
    const [stores, setStores] = useState([]);
    const [registers, setRegisters] = useState([]);
    const [scope, setScope] = useState('0000');
    const [scopeSource, setScopeSource] = useState('none');
    const [importAnchor, setImportAnchor] = useState(null);
    const [warnings, setWarnings] = useState([]);
    const [renderImg, setRenderImg] = useState('');
    const [renderOpen, setRenderOpen] = useState(false);
    // Alignment guides shown while dragging (mm positions of matched edges).
    const [guides, setGuides] = useState({ v: [], h: [] });
    // Canvas zoom (1 = fit the column). A4 invoice forms are unreadable at
    // fit scale, so they open zoomed in.
    const [zoom, setZoom] = useState(1);
    // Report script (PascalScript) viewer/editor + the objects it addresses
    // by name (deleting one of those breaks printing).
    const [scriptOpen, setScriptOpen] = useState(false);
    const [scriptText, setScriptText] = useState('');
    const [scriptedNames, setScriptedNames] = useState({});
    const scriptedRef = useRef({});
    scriptedRef.current = scriptedNames;

    const canvasRef = useRef(null);
    const fileRef = useRef(null);
    const dragRef = useRef(null);
    const modelRef = useRef(model);
    modelRef.current = model;
    const selectedIdsRef = useRef([]);
    selectedIdsRef.current = selectedIds;
    const rawModeRef = useRef(false);
    rawModeRef.current = rawMode;

    const isSft = props.isSft === true;
    const t = props.t || ((k, d) => d || k);
    // Sidebar-aware content class (same contract as the other screens:
    // .expanded clears the drawer, .not_expanded is the mobile layout).
    const containerClass = (props.screenWidth < 600 ? 'not_expanded ' : 'expanded ')
        + 'container_content _80perc ' + props.theme + ' container_content_' + props.theme;

    const round1 = (v) => Math.round(v * 100) / 100;

    // ------------------------------------------------------------------
    //  LOAD
    // ------------------------------------------------------------------

    const loadLayout = useCallback((targetScope) => {
        setLoading(true);
        SftGlobalData.post('uploads&action=getZplLayout',
            { key: props.api, sftUserId: props.sftUserId, scope: targetScope }).then((response) => {
            const data = response?.data;
            let opened = false;
            if (data) {
                setScopeSource(data.source || 'none');
                if (data.model) {
                    try {
                        const m = JSON.parse(data.model);
                        if (m && Array.isArray(m.elements)) {
                            setModel(m);
                            opened = true;
                        }
                    } catch (e) { /* fall through to the parser */ }
                }
                if (!opened && data.zpl) {
                    const parsed = parse(data.zpl, 8);
                    if (parsed.ok) {
                        setModel(parsed.model);
                        opened = true;
                    } else {
                        setRawMode(true);
                        setRawZpl(data.zpl);
                        setRawReason(parsed.reason || '');
                        opened = true;
                    }
                }
            }
            if (!opened) {
                setModel(emptyModel());
                setScopeSource('none');
            }
            // A freshly loaded scope starts its own history.
            historyRef.current = { past: [], future: [], lastPush: 0 };
            if (opened && (!data || !data.zpl || parse(data.zpl, 8).ok)) {
                setRawMode(false);
                setRawReason('');
            }
            setSelectedIds([]);
            setWarnings([]);
            setLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.api, props.sftUserId]);

    /** fr3 mode: fetch the file and open it as a DOM document. */
    const loadFr3 = useCallback(() => {
        setLoading(true);
        SftGlobalData.post('uploads&action=getFr3File', {
            key: props.api, sftUserId: props.sftUserId, source: fr3Source, name: fr3Name,
        }).then((response) => {
            if (response?.success && response?.data?.content_base64) {
                const xmlText = decodeFr3Base64(response.data.content_base64);
                const opened = openFr3(xmlText, 8);
                if (opened.ok) {
                    fr3Ref.current = { handle: opened.handle, originalXml: xmlText };
                    setModel(opened.model);
                    setLanes(opened.lanes || []);
                    setScriptText(opened.script || '');
                    setScriptedNames(opened.scriptedNames || {});
                    const warn = opened.warnings || [];
                    if (opened.hasScript) {
                        warn.push(t('upl_fr3_script_note',
                            'This layout contains a script (PascalScript) or event handlers. They are preserved exactly as they are. Note that the script addresses objects by their name, so removing an object it refers to can break printing — adding fields is always safe.'));
                    }
                    setWarnings(warn);
                    // A4 forms are unreadable at fit scale — open them larger.
                    setZoom(opened.model.widthMm >= 150 ? 2 : 1);
                } else {
                    fr3Ref.current = null;
                    setModel(emptyModel());
                    setWarnings([opened.reason === 'inherited'
                        ? t('upl_fr3_inherited', 'This layout inherits a parent form and cannot be imported')
                        : (t('upl_fr3_import_failed', 'Import failed: ') + opened.reason)]);
                }
            } else {
                fr3Ref.current = null;
                setModel(emptyModel());
                setWarnings([response?.message || 'Download failed']);
            }
            historyRef.current = { past: [], future: [], lastPush: 0 };
            setSelectedIds([]);
            setLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.api, props.sftUserId, fr3Source, fr3Name]);

    useEffect(() => {
        if (props.api === '' || !isSft) return;
        if (fr3Mode) loadFr3();
        else loadLayout('0000');

        SftGlobalData.post('uploads&action=listAssets',
            { key: props.api, sftUserId: props.sftUserId }).then((response) => {
            if (response?.data?.labels) setLabelList(response.data.labels);
            if (response?.data?.stores) setStores(response.data.stores);
            if (response?.data?.registers) setRegisters(response.data.registers);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.api, isSft, fr3Source, fr3Name]);

    const changeScope = (newScope) => {
        setScope(newScope);
        loadLayout(newScope);
    };

    const scopeLabel = (code) => {
        if (code === '0000') return t('upl_global', 'Global');
        if (code.substring(2) === '00') {
            const store = stores.find((entry) => entry.code === code.substring(0, 2));
            return t('upl_store', 'Store') + ' ' + code.substring(0, 2) + (store ? ' — ' + store.oms : '');
        }
        const register = registers.find((entry) => entry.code === code);
        return t('upl_register', 'Register') + ' ' + code + (register ? ' — ' + register.oms : '');
    };

    // ------------------------------------------------------------------
    //  MODEL HELPERS
    // ------------------------------------------------------------------

    const updateElement = useCallback((id, patch) => {
        setModel((m) => ({
            ...m,
            elements: m.elements.map((el) => (el.id === id ? { ...el, ...patch } : el)),
        }));
    }, []);

    // ------------------------------------------------------------------
    //  HISTORY (Ctrl+Z / Ctrl+Y). Snapshots push BEFORE a mutation; rapid
    //  bursts (typing, arrow-key nudges) coalesce into one undo step.
    // ------------------------------------------------------------------

    const historyRef = useRef({ past: [], future: [], lastPush: 0 });

    const pushHistory = useCallback(() => {
        const h = historyRef.current;
        const now = Date.now();
        if (now - h.lastPush < 400 && h.past.length > 0) { h.lastPush = now; return; }
        h.past.push(JSON.stringify(modelRef.current));
        if (h.past.length > 100) h.past.shift();
        h.future = [];
        h.lastPush = now;
    }, []);

    const undo = useCallback(() => {
        const h = historyRef.current;
        if (h.past.length === 0) return;
        h.future.push(JSON.stringify(modelRef.current));
        const prev = JSON.parse(h.past.pop());
        h.lastPush = 0;
        setModel(prev);
        setSelectedIds([]);
    }, []);

    const redo = useCallback(() => {
        const h = historyRef.current;
        if (h.future.length === 0) return;
        h.past.push(JSON.stringify(modelRef.current));
        const next = JSON.parse(h.future.pop());
        h.lastPush = 0;
        setModel(next);
        setSelectedIds([]);
    }, []);

    /** Properties-panel edits: same as updateElement but undoable. */
    const editElement = useCallback((id, patch) => {
        pushHistory();
        updateElement(id, patch);
    }, [pushHistory, updateElement]);

    const addElement = (type) => {
        pushHistory();
        const el = newElement(type, model);
        // fr3 label mode: the element becomes a fresh TfrxMemoView at save
        // time (applyFr3Edits), on the same band as the existing objects.
        if (fr3Mode) { el.isNew = true; el.frame = 0; el.frameW = 1; el.fontStyle = 0; el.fontName = 'Arial'; }
        setModel((m) => ({ ...m, elements: [...m.elements, el] }));
        setSelectedIds([el.id]);
    };

    const addField = (token) => {
        pushHistory();
        const el = newElement('text', model);
        el.text = fr3Mode ? '[DM1.PrintQ."' + token + '"]' : '[' + token + ']';
        // Land inside the visible page, not on top of the first band.
        if (fr3Mode && fr3Invoice) { el.y = Math.min(el.y + 4, (model.heightMm || 297) - 10); }
        if (fr3Mode) { el.isNew = true; el.frame = 0; el.frameW = 1; el.fontStyle = 0; el.fontName = 'Arial'; }
        setModel((m) => ({ ...m, elements: [...m.elements, el] }));
        setSelectedIds([el.id]);
    };

    /**
     * Multi-select geometry tools (FastReport's Alignment palette): all
     * operations are relative to the FIRST selected element, like the
     * FastReport designer. 0-dimension sides (lines) never change.
     */
    const alignSelection = (op) => {
        const ids = selectedIdsRef.current;
        if (ids.length < 2) return;
        const m = modelRef.current;
        const anchor = m.elements.find((el) => el.id === ids[0]);
        if (!anchor) return;
        pushHistory();
        const aw = anchor.w || 0;
        const ah = anchor.h || 0;
        setModel((mm) => ({
            ...mm,
            elements: mm.elements.map((el) => {
                if (ids.indexOf(el.id) < 0 || el.id === anchor.id) return el;
                const w = el.w || 0;
                const h = el.h || 0;
                switch (op) {
                    case 'left': return { ...el, x: round1(anchor.x) };
                    case 'hcenter': return { ...el, x: Math.max(0, round1(anchor.x + (aw - w) / 2)) };
                    case 'right': return { ...el, x: Math.max(0, round1(anchor.x + aw - w)) };
                    case 'top': return { ...el, y: round1(anchor.y) };
                    case 'vcenter': return { ...el, y: Math.max(0, round1(anchor.y + (ah - h) / 2)) };
                    case 'bottom': return { ...el, y: Math.max(0, round1(anchor.y + ah - h)) };
                    case 'width': return (w === 0 || aw === 0) ? el : { ...el, w: round1(aw) };
                    case 'height': return (h === 0 || ah === 0) ? el : { ...el, h: round1(ah) };
                    default: return el;
                }
            }),
        }));
    };

    const deleteSelected = () => {
        const ids = selectedIdsRef.current;
        if (ids.length === 0) return;
        // Only objects the report script addresses BY NAME are undeletable —
        // everything else (including fields added here) can go.
        const m = modelRef.current;
        const blocked = ids
            .map((id) => m.elements.find((el) => el.id === id))
            .filter((el) => el && el.name && scriptedRef.current[el.name]);
        if (blocked.length > 0) {
            notify(t('upl_del_scripted',
                'Used by the report script — delete would break printing:')
                + ' ' + blocked.map((el) => el.name).join(', '), false);
            return;
        }
        pushHistory();
        setModel((m) => ({ ...m, elements: m.elements.filter((el) => ids.indexOf(el.id) < 0) }));
        setSelectedIds([]);
    };

    // The keydown listener is bound once on mount; this ref keeps it pointed
    // at the current closure (which reads today's model + scripted names).
    const deleteSelectedRef = useRef(deleteSelected);
    deleteSelectedRef.current = deleteSelected;

    const selected = selectedIds.length === 1
        ? model.elements.find((el) => el.id === selectedIds[0]) || null
        : null;

    // ------------------------------------------------------------------
    //  DRAG / RESIZE — 0.5 mm grid, alignment guides, group move
    // ------------------------------------------------------------------

    // "Fit" scale (the page filling the 560 px column), times the user's zoom.
    // Every geometry calculation derives from pxPerMm, so dragging, guides and
    // the resize handle stay dot-accurate at any zoom.
    const fitPxPerMm = Math.min(12, 560 / (model.widthMm || 50));
    const pxPerMm = fitPxPerMm * zoom;

    const startDrag = (e, el, mode) => {
        e.preventDefault();
        e.stopPropagation();

        // Ctrl/Cmd-click: toggle membership, no drag.
        if (mode === 'move' && (e.ctrlKey || e.metaKey)) {
            setSelectedIds((ids) => (ids.indexOf(el.id) >= 0
                ? ids.filter((id) => id !== el.id)
                : [...ids, el.id]));
            return;
        }

        let group = selectedIdsRef.current;
        if (group.indexOf(el.id) < 0) {
            group = [el.id];
            setSelectedIds(group);
        }
        if (mode === 'resize') group = [el.id];

        // One undo step per drag gesture.
        pushHistory();

        const m = modelRef.current;
        dragRef.current = {
            pressedId: el.id, mode,
            startX: e.clientX, startY: e.clientY,
            items: group
                .map((id) => m.elements.find((entry) => entry.id === id))
                .filter(Boolean)
                .map((entry) => ({ id: entry.id, x: entry.x, y: entry.y, w: entry.w || 0, h: entry.h || 0 })),
        };
    };

    useEffect(() => {
        const move = (e) => {
            const drag = dragRef.current;
            if (!drag || drag.items.length === 0) return;
            const dxMm = (e.clientX - drag.startX) / pxPerMm;
            const dyMm = (e.clientY - drag.startY) / pxPerMm;
            const snap = (v) => Math.round(v * 2) / 2;
            const m = modelRef.current;

            if (drag.mode === 'move') {
                const pressed = drag.items.find((item) => item.id === drag.pressedId) || drag.items[0];
                let x = Math.max(0, Math.min(m.widthMm - 1, snap(pressed.x + dxMm)));
                let y = Math.max(0, Math.min(m.heightMm - 1, snap(pressed.y + dyMm)));

                // Alignment snap against elements OUTSIDE the dragged group:
                // pull to their edges within tolerance and show a guide line —
                // finer than the 0.5 mm grid, so it wins over it.
                const groupIds = drag.items.map((item) => item.id);
                const pressedEl = m.elements.find((entry) => entry.id === drag.pressedId);
                const pw = pressedEl ? (pressedEl.w || 0) : 0;
                const ph = pressedEl ? (pressedEl.h || 0) : 0;
                const tol = 0.6;
                const vGuides = [];
                const hGuides = [];
                m.elements.forEach((other) => {
                    if (groupIds.indexOf(other.id) >= 0) return;
                    const xCands = [other.x, other.x + (other.w || 0)];
                    for (let i = 0; i < xCands.length; i++) {
                        const cand = xCands[i];
                        if (Math.abs(x - cand) <= tol) { x = cand; vGuides.push(cand); break; }
                        if (Math.abs((x + pw) - cand) <= tol) { x = cand - pw; vGuides.push(cand); break; }
                    }
                    const yCands = [other.y, other.y + (other.h || 0)];
                    for (let i = 0; i < yCands.length; i++) {
                        const cand = yCands[i];
                        if (Math.abs(y - cand) <= tol) { y = cand; hGuides.push(cand); break; }
                        if (Math.abs((y + ph) - cand) <= tol) { y = cand - ph; hGuides.push(cand); break; }
                    }
                });
                setGuides({ v: vGuides, h: hGuides });

                // The pressed element lands on the snapped spot; the rest of
                // the group keeps its relative offsets.
                const deltaX = x - pressed.x;
                const deltaY = y - pressed.y;
                setModel((mm) => ({
                    ...mm,
                    elements: mm.elements.map((el) => {
                        const item = drag.items.find((entry) => entry.id === el.id);
                        if (!item) return el;
                        return {
                            ...el,
                            x: Math.max(0, round1(item.x + deltaX)),
                            y: Math.max(0, round1(item.y + deltaY)),
                        };
                    }),
                }));
            } else {
                // A 0-dimension is a LINE — the zero side stays locked so a
                // resize can't silently turn a line into a box.
                const item = drag.items[0];
                const w = item.w === 0 ? 0 : Math.max(1, snap(item.w + dxMm));
                const h = item.h === 0 ? 0 : Math.max(1, snap(item.h + dyMm));
                updateElement(item.id, { w, h });
            }
        };
        const up = () => {
            dragRef.current = null;
            setGuides({ v: [], h: [] });
        };
        document.addEventListener('mousemove', move);
        document.addEventListener('mouseup', up);
        return () => {
            document.removeEventListener('mousemove', move);
            document.removeEventListener('mouseup', up);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pxPerMm, updateElement]);

    // ------------------------------------------------------------------
    //  KEYBOARD: Del removes · arrows move 0.5 mm · Shift+arrow resizes.
    //  Applies to the whole selection; ignored while typing in an input.
    // ------------------------------------------------------------------

    useEffect(() => {
        const onKey = (e) => {
            if (rawModeRef.current) return;
            const tag = (e.target && e.target.tagName) || '';
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
                    || (e.target && e.target.isContentEditable)) return;
            // Undo/redo work regardless of the selection.
            if ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'Z')) {
                e.preventDefault();
                if (e.shiftKey) redo(); else undo();
                return;
            }
            if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || e.key === 'Y')) {
                e.preventDefault();
                redo();
                return;
            }
            const ids = selectedIdsRef.current;
            if (ids.length === 0) return;
            const step = 0.5;
            if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault();
                // One implementation for the key and the button, so the
                // script-reference guard cannot drift between them.
                deleteSelectedRef.current();
                return;
            }
            const arrows = {
                ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1],
            };
            if (!arrows[e.key]) return;
            e.preventDefault();
            pushHistory(); // coalesced: a burst of nudges = one undo step
            const dx = arrows[e.key][0];
            const dy = arrows[e.key][1];
            setModel((mm) => ({
                ...mm,
                elements: mm.elements.map((el) => {
                    if (ids.indexOf(el.id) < 0) return el;
                    if (e.shiftKey) {
                        // Grow/shrink toward the arrow; a line's 0-dim stays locked.
                        const patch = {};
                        if (dx !== 0 && (el.w || 0) !== 0) patch.w = Math.max(1, round1((el.w || 0) + dx * step));
                        if (dy !== 0 && (el.h || 0) !== 0) patch.h = Math.max(1, round1((el.h || 0) + dy * step));
                        return { ...el, ...patch };
                    }
                    return {
                        ...el,
                        x: Math.max(0, round1(el.x + dx * step)),
                        y: Math.max(0, round1(el.y + dy * step)),
                    };
                }),
            }));
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ------------------------------------------------------------------
    //  SAVE / RENDER / IMPORT
    // ------------------------------------------------------------------

    const notify = (text, ok) => {
        if (props.notify) props.notify(text, 5000, 'top-right', props.theme, ok ? 1 : 2);
    };

    const save = () => {
        setBusy(true);
        let zpl;
        if (rawMode) {
            zpl = rawZpl;
        } else {
            // Round-trip guard: a compiler bug must never brick a client's printers.
            const check = verifyRoundTrip(model);
            if (!check.ok) {
                setBusy(false);
                notify(t('upl_roundtrip_failed', 'Save blocked — compiled ZPL failed verification: ') + check.reason, false);
                return;
            }
            zpl = check.zpl;
        }
        SftGlobalData.post('uploads&action=saveZplLayout', {
            key: props.api,
            sftUserId: props.sftUserId,
            zpl,
            model: rawMode ? '' : JSON.stringify(model),
            scope,
        }).then((response) => {
            setBusy(false);
            if (response?.success) {
                setScopeSource('exact');
                notify(t('upl_saved_kasvar', 'Saved — devices pick it up on their next sync'), true);
            } else {
                notify((response?.message || 'Save failed'), false);
            }
        });
    };

    /** fr3 save: surgical DOM apply -> ASCII serialise -> verify -> upload. */
    const saveFr3 = () => {
        const state = fr3Ref.current;
        if (!state) return;
        setBusy(true);
        applyFr3Edits(state.handle, modelRef.current.elements);
        const out = serializeFr3(state.handle);
        // Guard: the edited file must reopen with the same element count.
        const check = openFr3(out, 8);
        if (!check.ok || check.model.elements.length !== modelRef.current.elements.length) {
            setBusy(false);
            notify(t('upl_fr3_save_guard', 'Save blocked — the edited file failed verification'), false);
            return;
        }
        const action = fr3Invoice ? 'uploadInvoiceForm' : 'uploadLabelLayout';
        SftGlobalData.post('uploads&action=' + action, {
            key: props.api, sftUserId: props.sftUserId, name: fr3Name, content: encodeBase64(out),
        }).then((response) => {
            setBusy(false);
            if (response?.success) {
                notify(t('upl_fr3_saved', 'Saved ') + fr3Name, true);
                loadFr3(); // fresh handle + originals for further edits
            } else {
                notify(response?.message || 'Save failed', false);
            }
        });
    };

    /**
     * Page geometry. Margins move the origin every element is measured from,
     * so the document is re-opened from the edited XML afterwards instead of
     * patching coordinates by hand - pending element edits are folded in
     * first so nothing is lost.
     */
    const applyPageSettings = (patch) => {
        const state = fr3Ref.current;
        if (!state) return;
        pushHistory();
        applyFr3Edits(state.handle, modelRef.current.elements);
        setFr3Page(state.handle, patch);
        const xml = serializeFr3(state.handle);
        const reopened = openFr3(xml, 8);
        if (!reopened.ok) {
            notify(t('upl_page_failed', 'Page settings could not be applied'), false);
            return;
        }
        fr3Ref.current = { handle: reopened.handle, originalXml: state.originalXml };
        setModel(reopened.model);
        setLanes(reopened.lanes || []);
        setSelectedIds([]);
    };

    /** The pre-edit file as a download — the manual undo of last resort. */
    const downloadOriginal = () => {
        const state = fr3Ref.current;
        if (!state) return;
        const blob = new Blob([state.originalXml], { type: 'application/xml' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = fr3Name;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const clearOverride = () => {
        if (!window.confirm(t('upl_confirm_clear_zpl', 'Remove this override? The scope inherits again.'))) return;
        setBusy(true);
        SftGlobalData.post('uploads&action=clearZplLayout', {
            key: props.api, sftUserId: props.sftUserId, scope,
        }).then((response) => {
            setBusy(false);
            if (response?.success) {
                notify(t('upl_override_cleared', 'Override removed'), true);
                loadLayout(scope);
            } else {
                notify(response?.message || 'Failed', false);
            }
        });
    };

    const exactRender = () => {
        setBusy(true);
        const zpl = substituteSample(rawMode ? rawZpl : compile(model));
        SftGlobalData.post('uploads&action=labelaryRender', {
            key: props.api,
            sftUserId: props.sftUserId,
            zpl,
            dpmm: model.dpmm || 8,
            widthMm: model.widthMm,
            heightMm: model.heightMm,
        }).then((response) => {
            setBusy(false);
            if (response?.success && response?.data?.image) {
                setRenderImg(response.data.image);
                setRenderOpen(true);
            } else {
                notify(response?.message || 'Render failed', false);
            }
        });
    };

    const applyImport = (xmlText, sourceName) => {
        const result = importFr3(xmlText, model.dpmm || 8);
        if (!result.ok) {
            if (result.reason === 'inherited') {
                notify(t('upl_fr3_inherited', 'This layout inherits a parent form and cannot be imported'), false);
            } else {
                notify(t('upl_fr3_import_failed', 'Import failed: ') + result.reason, false);
            }
            return;
        }
        pushHistory();
        setModel(result.model);
        setWarnings(result.warnings || []);
        setSelectedIds([]);
        setRawMode(false);
        notify(t('upl_fr3_imported', 'Imported ') + sourceName + ' — ' + result.model.elements.length + ' ' + t('upl_elements', 'elements'), true);
    };

    const importFromServer = (name) => {
        setImportAnchor(null);
        setBusy(true);
        SftGlobalData.post('uploads&action=getFr3File', {
            key: props.api, sftUserId: props.sftUserId, source: 'labels', name,
        }).then((response) => {
            setBusy(false);
            if (response?.success && response?.data?.content_base64) {
                applyImport(decodeFr3Base64(response.data.content_base64), name);
            } else {
                notify(response?.message || 'Download failed', false);
            }
        });
    };

    const importFromFile = (e) => {
        setImportAnchor(null);
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const bytes = new Uint8Array(ev.target.result);
            let binary = '';
            for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
            applyImport(decodeFr3Base64(btoa(binary)), file.name);
        };
        reader.readAsArrayBuffer(file);
    };

    const tryVisualFromRaw = () => {
        const parsed = parse(rawZpl, model.dpmm || 8);
        if (parsed.ok) {
            pushHistory();
            setModel(parsed.model);
            setRawMode(false);
            setRawReason('');
        } else {
            notify(t('upl_parse_failed', 'Still not parseable: ') + parsed.reason, false);
        }
    };

    // ------------------------------------------------------------------
    //  RENDER HELPERS
    // ------------------------------------------------------------------

    /**
     * A believable value for an fr3 dataset field. Known PrintQ columns come
     * from the table above; anything else is guessed from the name so a
     * client-specific column still previews as data instead of plumbing.
     */
    const fr3Sample = (field) => {
        const key = String(field).toLowerCase();
        if (Object.prototype.hasOwnProperty.call(FR3_SAMPLE_VALUES, key)) {
            return FR3_SAMPLE_VALUES[key];
        }
        if (/datum|date|dag/.test(key)) return '26/08/2026';
        if (/^t_|prijs|price|bedrag|totaal|netto|btw|korting|vp$|wsp$|ep$/.test(key)) return '24,95';
        if (/aantal|qty|stuks|lijnen/.test(key)) return '2';
        if (/barcode|ean/.test(key)) return '5400000123457';
        if (/nr$|nummer|number/.test(key)) return '10248';
        return key.charAt(0).toUpperCase() + key.slice(1);
    };

    const displayText = (text) => {
        if (!sampleOn) return text;
        let out = text;
        if (fr3Mode) {
            // FastReport references: [DataSet."field"] on their own and
            // <DataSet."field"> inside expressions. Everything around them
            // (functions, IIF, literals) stays visible - it is not evaluated.
            const FIELD = '(?:"([A-Za-z0-9_]+)"|([A-Za-z0-9_]+))';
            out = out
                .replace(new RegExp('\[\s*[A-Za-z0-9_.]+\.' + FIELD + '\s*\]', 'g'),
                    (m, quoted, bare) => fr3Sample(quoted || bare))
                .replace(new RegExp('<\s*[A-Za-z0-9_.]+\.' + FIELD + '\s*>', 'g'),
                    (m, quoted, bare) => fr3Sample(quoted || bare));
            return out;
        }
        Object.keys(SAMPLE_VALUES).forEach((token) => {
            out = out.split('[' + token + ']').join(SAMPLE_VALUES[token]);
        });
        return out;
    };

    const isDark = props.theme === 'dark';
    const panelBg = isDark ? Colors.dark_accent : '#FFFFFF';
    const panelBorder = isDark ? Colors.dark_accent_darker : Colors.light_accent_darker;
    const inkFaint = isDark ? Colors.dark_text_accent : '#6B7280';

    const sizePresets = [
        { label: '50 × 25 mm', w: 50, h: 25 },
        { label: '57 × 31 mm', w: 57, h: 31 },
        { label: '62 × 29 mm', w: 62, h: 29 },
        { label: '90 × 50 mm', w: 90, h: 50 },
    ];
    const presetValue = (() => {
        const hit = sizePresets.find((preset) => preset.w === model.widthMm && preset.h === model.heightMm);
        return hit ? hit.label : 'custom';
    })();

    // FastReport paints a caption bar per band. The page here is WYSIWYG, so
    // the captions live in a gutter beside the page instead of taking vertical
    // space: same information, zero effect on the printed geometry.
    const BAND_TINT = {
        TfrxReportTitle:  '#7E57C2', TfrxReportSummary: '#5E35B1',
        TfrxPageHeader:   '#0071BB', TfrxPageFooter:    '#0288D1',
        TfrxHeader:       '#00838F', TfrxFooter:        '#00838F',
        TfrxMasterData:   '#2E7D32', TfrxDetailData:    '#388E3C',
        TfrxSubdetailData:'#43A047', TfrxGroupHeader:   '#EF6C00',
        TfrxGroupFooter:  '#F57C00', TfrxColumnHeader:  '#6D4C41',
        TfrxColumnFooter: '#6D4C41', TfrxChild:         '#546E7A',
        TfrxOverlay:      '#8E24AA',
    };
    const bandTint = (lane) => BAND_TINT[lane.tag] || '#607D8B';

    /**
     * Caption geometry: bands that start at (nearly) the same height would
     * cover each other's caption, so they step sideways like tabs.
     */
    const laneCaptions = () => {
        const rows = lanes.map((lane, i) => ({ lane, i,
            top: lane.topMm * pxPerMm, band: lane.heightMm * pxPerMm }))
            .sort((a, b) => a.top - b.top || a.i - b.i);

        // Bands that begin at the same height (an invoice's group header and
        // its master data do) share the gutter as COLUMNS - overlapping bars
        // would print their labels over each other.
        const groups = [];
        rows.forEach((row) => {
            const open = groups[groups.length - 1];
            if (open && Math.abs(row.top - open.top) < 1.5) open.rows.push(row);
            else groups.push({ top: row.top, rows: [row] });
        });

        const pageBottom = model.heightMm * pxPerMm;
        groups.forEach((group, gi) => {
            // A caption never grows into the next one: that is the other way
            // labels used to collide (a 0 mm band inflated to a readable bar).
            const nextTop = gi + 1 < groups.length ? groups[gi + 1].top : pageBottom;
            const columns = group.rows.length;
            group.rows.forEach((row, ri) => {
                row.widthPct = 100 / columns;
                row.leftPct = ri * (100 / columns);
                // Clamped against THIS row's own top (rows in one group may sit
                // a fraction apart) and NOT floored: when the next band is a
                // hair away the caption shrinks below the readable minimum
                // rather than covering it.
                const room = Math.max(1, nextTop - row.top);
                row.height = Math.min(Math.max(row.band, 3), room);
                // Text only where it actually fits, so nothing is clipped
                // mid-glyph; the tooltip always carries the full name.
                row.showKind = row.height >= 13;
                row.showName = row.height >= 27 && columns === 1;
                row.showMm = row.height >= 42 && columns === 1;
            });
        });
        return groups.reduce((all, group) => all.concat(group.rows), []);
    };

    const propRow = (label, control) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Typography sx={{ fontSize: 14, color: inkFaint, whiteSpace: 'nowrap' }}>{label}</Typography>
            {control}
        </Box>
    );

    /** Geometry row: an icon label (with tooltip) + the mm unit. */
    const iconRow = (icon, tooltip, control) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Typography component="span" title={tooltip}
                sx={{ color: inkFaint, display: 'flex', alignItems: 'center', gap: 0.4, fontSize: 12.5, whiteSpace: 'nowrap' }}>
                {icon} mm
            </Typography>
            {control}
        </Box>
    );

    const numField = (value, onChange, width) => (
        <TextField
            size="small" type="number" value={value}
            onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
            inputProps={{ step: 0.5, style: { fontSize: 14.5, padding: '8px 10px', width: width || 76 } }}
        />
    );

    const toolButton = (label, onClick, solid) => (
        <Button
            key={label}
            size="small"
            variant={solid ? 'contained' : 'outlined'}
            onClick={onClick}
            disabled={busy}
            sx={{
                fontSize: 13, textTransform: 'none', py: 0.45, px: 1.5, minWidth: 0,
                backgroundColor: solid ? Colors.def_green : undefined,
                borderColor: Colors.sft_blue, color: solid ? '#fff' : Colors.sft_blue,
                '&:hover': { backgroundColor: solid ? '#4CA64C' : undefined },
            }}
        >
            {label}
        </Button>
    );

    // ------------------------------------------------------------------
    //  GATES
    // ------------------------------------------------------------------

    if (!isSft) {
        return (
            <div className={containerClass}>
                <Typography sx={{ p: 4, textAlign: 'center' }}>
                    {t('upl_sft_only', 'This section is only available to SoftTouch users.')}
                </Typography>
            </div>
        );
    }

    if (loading) {
        return (
            <div className={containerClass}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <TailSpin height="40" width="40" color={Colors.sft_blue} ariaLabel="loading" />
                </Box>
            </div>
        );
    }

    // ------------------------------------------------------------------
    //  SCREEN
    // ------------------------------------------------------------------

    const zplOut = fr3Mode ? '' : (rawMode ? rawZpl : compile(model));

    return (
        <div className={containerClass}>
            {/* Breadcrumb */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Button size="small" sx={{ textTransform: 'none', color: Colors.sft_blue }}
                    onClick={() => navigate('/' + props.lang + '/mobile/uploads')}>
                    ‹ {t('upl_back_uploads', 'Back to uploads')}
                </Button>
                <Typography sx={{ fontWeight: 700 }}>
                    MyFasMan Mobile › {t('upl_uploads', 'Uploads')} › {t('upl_designer', 'Designer')}
                </Typography>
                <Typography sx={{ fontSize: 13.5, color: inkFaint }}>
                    {fr3Mode
                        ? (fr3Name + (fr3Invoice ? ' · ' + t('upl_touchup', 'invoice touch-up') : ' · .fr3'))
                        : ('mobileZPLcode · ' + scopeLabel(scope))}
                </Typography>
            </Box>

            {/* Toolbar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                {fr3Mode && (
                <React.Fragment>
                    <Typography sx={{ fontSize: 13.5, color: inkFaint }}>
                        {model.widthMm} × {model.heightMm} mm
                        {fr3Invoice ? ' · ' + t('upl_touchup_note', 'move / resize / restyle only') : ''}
                    </Typography>
                    {toolButton(t('upl_download_original', 'Download original'), downloadOriginal)}
                    <Box sx={{ flex: 1 }} />
                    {fr3Ref.current && toolButton(t('upl_script_btn', 'Script…'), () => setScriptOpen(true))}
                    {fr3Ref.current && toolButton(t('upl_save_fr3', 'Save') + ' ' + fr3Name, saveFr3, true)}
                </React.Fragment>
                )}
                {!fr3Mode && (
                <React.Fragment>
                <Select
                    size="small" value={scope} sx={{ fontSize: 13.5, maxWidth: 240 }}
                    onChange={(e) => changeScope(e.target.value)}
                >
                    <MenuItem value="0000" sx={{ fontSize: 13.5 }}>{t('upl_global', 'Global')}</MenuItem>
                    {stores.map((store) => (
                        <MenuItem key={'s' + store.code} value={store.code + '00'} sx={{ fontSize: 13.5 }}>
                            {t('upl_store', 'Store')} {store.code} — {store.oms}
                        </MenuItem>
                    ))}
                    {registers.map((register) => (
                        <MenuItem key={'r' + register.code} value={register.code} sx={{ fontSize: 13.5 }}>
                            {t('upl_register', 'Register')} {register.code} — {register.oms}
                        </MenuItem>
                    ))}
                </Select>
                <Select
                    size="small" value={presetValue} sx={{ fontSize: 13.5 }}
                    onChange={(e) => {
                        const preset = sizePresets.find((entry) => entry.label === e.target.value);
                        if (preset) setModel((m) => ({ ...m, widthMm: preset.w, heightMm: preset.h }));
                    }}
                >
                    {sizePresets.map((preset) => (
                        <MenuItem key={preset.label} value={preset.label} sx={{ fontSize: 13.5 }}>{preset.label}</MenuItem>
                    ))}
                    <MenuItem value="custom" sx={{ fontSize: 13.5 }}>{t('upl_custom', 'Custom')}</MenuItem>
                </Select>
                {numField(model.widthMm, (v) => setModel((m) => ({ ...m, widthMm: Math.max(10, v) })), 52)}
                <Typography sx={{ fontSize: 13.5 }}>×</Typography>
                {numField(model.heightMm, (v) => setModel((m) => ({ ...m, heightMm: Math.max(10, v) })), 52)}
                <Typography sx={{ fontSize: 13.5, color: inkFaint }}>mm</Typography>
                <Select
                    size="small" value={model.dpmm || 8} sx={{ fontSize: 13.5 }}
                    onChange={(e) => setModel((m) => ({ ...m, dpmm: e.target.value }))}
                >
                    <MenuItem value={6} sx={{ fontSize: 13.5 }}>6 dpmm (152 dpi)</MenuItem>
                    <MenuItem value={8} sx={{ fontSize: 13.5 }}>8 dpmm (203 dpi)</MenuItem>
                    <MenuItem value={12} sx={{ fontSize: 13.5 }}>12 dpmm (300 dpi)</MenuItem>
                </Select>
                <FormControlLabel
                    sx={{ '& .MuiTypography-root': { fontSize: 13.5 } }}
                    control={<Switch size="small" checked={sampleOn} onChange={() => setSampleOn(!sampleOn)} />}
                    label={t('upl_sample', 'Sample data')}
                />
                {toolButton(t('upl_import_fr3', 'Import .fr3'), (e) => setImportAnchor(e.currentTarget))}
                {toolButton(t('upl_exact_render', 'Exact render (Labelary)'), exactRender)}
                {toolButton(rawMode ? t('upl_try_visual', 'Try visual editor') : t('upl_raw_zpl', 'Raw ZPL'),
                    rawMode ? tryVisualFromRaw : () => { setRawZpl(compile(model)); setRawMode(true); })}
                {scope !== '0000' && scopeSource === 'exact'
                    && toolButton(t('upl_clear_zpl_override', 'Remove override'), clearOverride)}
                <Box sx={{ flex: 1 }} />
                {toolButton(t('upl_save_kasvar', 'Save to kasvar'), save, true)}
                </React.Fragment>
                )}
            </Box>

            <Menu anchorEl={importAnchor} open={Boolean(importAnchor)} onClose={() => setImportAnchor(null)}>
                {labelList.map((file) => (
                    <MenuItem key={file.name} sx={{ fontSize: 13.5 }} onClick={() => importFromServer(file.name)}>
                        {file.name}
                    </MenuItem>
                ))}
                <MenuItem sx={{ fontSize: 13.5 }} onClick={() => { if (fileRef.current) fileRef.current.click(); }}>
                    {t('upl_local_file', 'Local .fr3 file…')}
                </MenuItem>
            </Menu>
            <input type="file" accept=".fr3" style={{ display: "none" }} ref={fileRef} onChange={importFromFile} />

            {scope !== '0000' && scopeSource !== 'exact' && (
                <Box sx={{ mb: 1, p: 1, borderRadius: 1, backgroundColor: '#E1EFF8', color: '#075985', fontSize: 13.5 }}>
                    {t('upl_inherited_note', 'This scope has no own layout yet — showing the inherited one (')}
                    {scopeSource === 'store' ? t('upl_store', 'Store') : t('upl_global', 'Global')}
                    {t('upl_inherited_note2', '). Saving creates an override for this scope.')}
                </Box>
            )}
            {warnings.length > 0 && (
                <Box sx={{ mb: 1, p: 1, borderRadius: 1, backgroundColor: '#FBF0DA', color: '#7A5D00', fontSize: 13.5 }}>
                    {warnings.map((warning, i) => (<div key={i}>{warning}</div>))}
                </Box>
            )}
            {rawMode && rawReason !== '' && (
                <Box sx={{ mb: 1, p: 1, borderRadius: 1, backgroundColor: '#FBF0DA', color: '#7A5D00', fontSize: 13.5 }}>
                    {t('upl_raw_because', 'Opened in raw mode — the current layout could not be modelled: ')}{rawReason}
                </Box>
            )}

            {rawMode ? (
                <TextField
                    multiline fullWidth minRows={16} value={rawZpl}
                    onChange={(e) => setRawZpl(e.target.value)}
                    inputProps={{ style: { fontFamily: 'Consolas, monospace', fontSize: 14 } }}
                />
            ) : (
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '150px 1fr 230px' },
                    border: '1px solid ' + panelBorder, borderRadius: 2, overflow: 'hidden', backgroundColor: panelBg,
                }}>
                    {/* Toolbox — every fr3 takes new text fields (fresh
                        TfrxMemoView nodes at save, attached to the band they
                        are dropped in). Invoice touch-up still refuses
                        DELETES: the report script addresses objects by name. */}
                    <Box sx={{ borderRight: { md: '1px solid ' + panelBorder }, p: 1.2 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: inkFaint, mb: 0.6 }}>TOOLBOX</Typography>
                        {(fr3Mode
                            ? [['text', t('upl_el_text', 'Text')]]
                            : [['text', t('upl_el_text', 'Text')], ['barcode', t('upl_el_barcode', 'Barcode')],
                               ['line', t('upl_el_line', 'Line')], ['box', t('upl_el_box', 'Box')],
                               ['logo', t('upl_el_logo', 'Logo')]]).map(([type, label]) => (
                            <Button key={type} fullWidth size="small" variant="outlined"
                                onClick={() => addElement(type)}
                                sx={{ justifyContent: 'flex-start', fontSize: 13, textTransform: 'none', mb: 0.5, borderColor: panelBorder, color: 'inherit' }}>
                                ＋ {label}
                            </Button>
                        ))}
                        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: inkFaint, mt: 1.4, mb: 0.6 }}>
                            {t('upl_fields', 'FIELDS')}
                        </Typography>
                        {(fr3Mode ? (fr3Invoice ? FR3_INVOICE_FIELDS : FR3_LABEL_FIELDS) : CLASSIC_TOKENS).map((token) => (
                            <Button key={token} fullWidth size="small"
                                onClick={() => addField(token)}
                                sx={{ justifyContent: 'flex-start', fontSize: 12.5, textTransform: 'none', mb: 0.2, color: Colors.sft_blue, py: 0 }}>
                                [{token}]
                            </Button>
                        ))}
                        {/* WHAT the [TITLE]/[LINE…] fields contain is set in the
                            Kleefetiketten module settings (settLabelTitleOne/Two,
                            settLabelFieldOne..Eight). */}
                        {!fr3Mode &&
                        <Button fullWidth size="small" variant="outlined"
                            onClick={() => navigate('/' + props.lang + '/module/Label')}
                            sx={{ mt: 1, fontSize: 11.5, textTransform: 'none', borderColor: Colors.sft_blue, color: Colors.sft_blue, lineHeight: 1.3 }}>
                            {t('upl_fields_config', 'Field contents… (Kleefetiketten)')}
                        </Button>}
                    </Box>

                    {/* Canvas */}
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, backgroundColor: isDark ? Colors.dark_accent_darkest : Colors.light_accent, minWidth: 0 }}
                        onMouseDown={() => setSelectedIds([])}>
                        {/* Zoom — a page zoomed past the column scrolls inside
                            the wrapper below instead of stretching the grid. */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, alignSelf: 'flex-end' }}
                            onMouseDown={(e) => e.stopPropagation()}>
                            <IconButton size="small" title={t('upl_zoom_out', 'Zoom out')}
                                onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.25) * 100) / 100))}>
                                <ZoomOutIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                            <Typography sx={{ fontSize: 12.5, color: inkFaint, minWidth: 42, textAlign: 'center' }}>
                                {Math.round(zoom * 100)}%
                            </Typography>
                            <IconButton size="small" title={t('upl_zoom_in', 'Zoom in')}
                                onClick={() => setZoom((z) => Math.min(6, Math.round((z + 0.25) * 100) / 100))}>
                                <ZoomInIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                            <Button size="small" onClick={() => setZoom(1)}
                                sx={{ fontSize: 12, textTransform: 'none', minWidth: 0, color: Colors.sft_blue }}>
                                {t('upl_zoom_fit', 'Fit')}
                            </Button>
                        </Box>
                        <Box sx={{ maxWidth: '100%', overflow: 'auto', alignSelf: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        {/* Band captions (FastReport's band bars). They sit
                            BESIDE the page, so naming a band never shifts the
                            printed content. Heights follow the real bands and
                            scale with the zoom; bands that start at the same
                            height step sideways so both stay readable. */}
                        {fr3Mode && lanes.length > 0 && (
                            <div style={{
                                position: 'relative', flex: '0 0 auto',
                                width: 132, marginRight: 8,
                                height: model.heightMm * pxPerMm,
                                // Opaque, so a caption tint composites against
                                // the gutter and never against another caption.
                                background: isDark ? Colors.dark_accent_darkest : '#FFFFFF',
                                borderRadius: 3,
                            }}>
                                {laneCaptions().map((row) => {
                                    const tint = bandTint(row.lane);
                                    return (
                                        <div key={'cap' + row.i}
                                            title={row.lane.name + ' — ' + row.lane.heightMm + ' mm'}
                                            style={{
                                                position: 'absolute', top: row.top,
                                                left: row.leftPct + '%', width: row.widthPct + '%',
                                                height: row.height,
                                                background: tint + '1F',
                                                borderLeft: '3px solid ' + tint,
                                                borderTop: '1px solid ' + tint + '66',
                                                borderRadius: '3px 0 0 3px',
                                                overflow: 'hidden', boxSizing: 'border-box',
                                                padding: row.showKind ? '1px 3px' : 0,
                                            }}>
                                            {row.showKind && (
                                                <div style={{
                                                    fontSize: 10, fontWeight: 700, lineHeight: 1.15,
                                                    color: tint, whiteSpace: 'nowrap',
                                                    overflow: 'hidden', textOverflow: 'ellipsis',
                                                }}>{row.lane.kind || row.lane.name}</div>
                                            )}
                                            {row.showName && row.lane.label && (
                                                <div style={{
                                                    fontSize: 9.5, lineHeight: 1.2, color: inkFaint,
                                                    whiteSpace: 'nowrap', overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}>{row.lane.label}</div>
                                            )}
                                            {row.showMm && (
                                                <div style={{ fontSize: 9, color: inkFaint, marginTop: 1 }}>
                                                    {row.lane.heightMm} mm
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <div
                            ref={canvasRef}
                            style={{
                                position: 'relative',
                                width: model.widthMm * pxPerMm,
                                height: model.heightMm * pxPerMm,
                                background: '#FFFFFF',
                                backgroundImage: 'linear-gradient(#EDF2F6 1px, transparent 1px), linear-gradient(90deg, #EDF2F6 1px, transparent 1px)',
                                backgroundSize: (5 * pxPerMm) + 'px ' + (5 * pxPerMm) + 'px',
                                border: '1.5px solid #C9D2DA',
                                borderRadius: 3,
                                boxShadow: '0 2px 8px rgba(0,0,0,.12)',
                                overflow: 'hidden',
                                color: '#111',
                            }}
                        >
                            {/* fr3 band lanes (rendered behind the objects) */}
                            {fr3Mode && lanes.map((lane, i) => (
                                <div key={'lane' + i} style={{
                                    position: 'absolute', left: 0, right: 0,
                                    top: lane.topMm * pxPerMm,
                                    height: Math.max(2, lane.heightMm * pxPerMm),
                                    background: i % 2 === 0 ? 'rgba(0,113,187,.035)' : 'rgba(0,113,187,.07)',
                                    borderTop: '1px dashed ' + bandTint(lane),
                                    pointerEvents: 'none',
                                }} />
                            ))}
                            {model.elements.map((el) => {
                                const sel = selectedIds.indexOf(el.id) >= 0;
                                // ZPL rotates around ^FO; the canvas rotates
                                // around the element centre — visual feedback,
                                // not dot-exact placement.
                                const rotDeg = el.rotation === 'R' ? 90 : (el.rotation === 'I' ? 180 : (el.rotation === 'B' ? 270 : 0));
                                const common = {
                                    position: 'absolute',
                                    left: el.x * pxPerMm,
                                    top: el.y * pxPerMm,
                                    cursor: 'move',
                                    border: sel ? '1px dashed ' + Colors.sft_blue : '1px dashed transparent',
                                    background: sel ? 'rgba(0,113,187,.07)' : 'transparent',
                                    boxSizing: 'border-box',
                                    transform: rotDeg !== 0 ? 'rotate(' + rotDeg + 'deg)' : undefined,
                                    transformOrigin: 'center center',
                                };
                                let content = null;
                                if (el.type === 'text') {
                                    const fontPx = ((el.font || 24) / (model.dpmm || 8)) * pxPerMm;
                                    // fr3 memo Frame borders (bitmask 1 left /
                                    // 2 right / 4 top / 8 bottom) — most
                                    // templates draw their rules this way.
                                    // NEVER mix the border shorthand with the
                                    // side longhands here: React re-sets the
                                    // shorthand on select/deselect, which
                                    // clobbers the longhand sides in the
                                    // CSSOM while its diff skips re-applying
                                    // their unchanged values — the frames
                                    // would vanish after a click.
                                    const fb = el.frame || 0;
                                    const framePx = Math.max(1, ((el.frameW || 1) / (model.dpmm || 8)) * pxPerMm);
                                    const side = (bit) => ((fb & bit)
                                        ? framePx + 'px solid #111'
                                        : (sel ? '1px dashed ' + Colors.sft_blue : '1px dashed transparent'));
                                    content = (
                                        <div style={{
                                            ...common,
                                            border: undefined,
                                            borderLeft: side(1),
                                            borderRight: side(2),
                                            borderTop: side(4),
                                            borderBottom: side(8),
                                            background: el.background ? '#FFFFFF' : common.background,
                                            width: (el.w || 10) * pxPerMm,
                                            // The element's designed height is a HARD clip: text
                                            // that does not fit the width cuts off visibly instead
                                            // of auto-growing the field (the data stays intact -
                                            // only the user resizes fields).
                                            height: el.h ? el.h * pxPerMm : undefined,
                                            minHeight: el.h ? undefined : fontPx,
                                            fontSize: fontPx * 0.82,
                                            lineHeight: 1.1,
                                            fontFamily: (el.fontName ? '"' + el.fontName + '", ' : '')
                                                + 'Arial, Helvetica, sans-serif',
                                            // Font.Style set: fsBold=1, fsItalic=2, fsUnderline=4.
                                            fontWeight: ((el.fontStyle || 0) & 1) ? 700 : 400,
                                            fontStyle: ((el.fontStyle || 0) & 2) ? 'italic' : 'normal',
                                            textDecoration: ((el.fontStyle || 0) & 4) ? 'underline' : 'none',
                                            textAlign: el.align === 'C' ? 'center'
                                                : (el.align === 'R' ? 'right'
                                                    : (el.align === 'J' ? 'justify' : 'left')),
                                            overflow: 'hidden',
                                            whiteSpace: (el.maxLines || 1) > 1 ? 'normal' : 'nowrap',
                                        }}>
                                            {displayText(el.text || '')}
                                        </div>
                                    );
                                } else if (el.type === 'barcode') {
                                    content = (
                                        <div style={{
                                            ...common,
                                            background: el.background ? '#FFFFFF' : common.background,
                                            width: (el.w || 20) * pxPerMm,
                                            height: (el.h || 10) * pxPerMm,
                                        }}>
                                            <div style={{
                                                height: '78%',
                                                background: 'repeating-linear-gradient(90deg,#111 0 2px,transparent 2px 4px,#111 4px 5px,transparent 5px 9px,#111 9px 12px,transparent 12px 14px)',
                                            }} />
                                            {el.interpretation && (
                                                <div style={{ fontSize: Math.max(7, 2.2 * pxPerMm * 0.4), textAlign: 'center', letterSpacing: 2 }}>
                                                    {sampleOn ? (fr3Mode ? displayText(el.data || '') : SAMPLE_VALUES.BARCODE) : el.data}
                                                </div>
                                            )}
                                        </div>
                                    );
                                } else if (el.type === 'box') {
                                    // Thickness: printer dots -> mm -> px (a
                                    // 2-dot line at 8 dpmm is 0.25 mm).
                                    const thickPx = Math.max(1, ((el.thickness || 1) / (model.dpmm || 8)) * pxPerMm);
                                    const isHLine = (el.h || 0) === 0;
                                    const isVLine = !isHLine && (el.w || 0) === 0;
                                    if (isHLine || isVLine) {
                                        // A line renders as a filled bar of its thickness.
                                        content = (
                                            <div style={{
                                                ...common,
                                                width: isVLine ? Math.max(2, thickPx) : (el.w || 10) * pxPerMm,
                                                height: isHLine ? Math.max(2, thickPx) : (el.h || 5) * pxPerMm,
                                                background: '#111',
                                                border: sel ? '1px dashed ' + Colors.sft_blue : 'none',
                                            }} />
                                        );
                                    } else {
                                        content = (
                                            <div style={{
                                                ...common,
                                                width: (el.w || 10) * pxPerMm,
                                                height: (el.h || 5) * pxPerMm,
                                                border: sel ? '1px dashed ' + Colors.sft_blue
                                                    : (el.fill === 'white' ? '1px dashed #C9D2DA' : thickPx + 'px solid #111'),
                                                background: el.fill === 'white' ? '#FFFFFF' : 'transparent',
                                            }} />
                                        );
                                    }
                                } else if (el.type === 'logo') {
                                    content = (
                                        <div style={{
                                            ...common,
                                            width: (el.w || 10) * pxPerMm,
                                            height: (el.h || 5) * pxPerMm,
                                            border: '1px dashed #9DBFD4',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: 10, color: '#5B7A8C', fontWeight: 700, letterSpacing: 1,
                                        }}>
                                            LOGO
                                        </div>
                                    );
                                }
                                return (
                                    <div key={el.id} onMouseDown={(e) => startDrag(e, el, 'move')}>
                                        {content}
                                        {sel && selectedIds.length === 1 && (
                                            <div
                                                onMouseDown={(e) => startDrag(e, el, 'resize')}
                                                style={{
                                                    position: 'absolute',
                                                    left: (el.x + (el.w || 4)) * pxPerMm - 4,
                                                    top: (el.y + (el.h || 3)) * pxPerMm - 4,
                                                    width: 8, height: 8,
                                                    background: Colors.sft_blue,
                                                    cursor: 'nwse-resize',
                                                    borderRadius: 1,
                                                }}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                            {/* Alignment guides while dragging */}
                            {guides.v.map((gx, i) => (
                                <div key={'gv' + i} style={{
                                    position: 'absolute', left: gx * pxPerMm, top: 0, bottom: 0,
                                    width: 0, borderLeft: '1px dashed ' + Colors.sft_blue,
                                    pointerEvents: 'none', zIndex: 5,
                                }} />
                            ))}
                            {guides.h.map((gy, i) => (
                                <div key={'gh' + i} style={{
                                    position: 'absolute', top: gy * pxPerMm, left: 0, right: 0,
                                    height: 0, borderTop: '1px dashed ' + Colors.sft_blue,
                                    pointerEvents: 'none', zIndex: 5,
                                }} />
                            ))}
                        </div>
                        </div>
                        </Box>
                        <Typography sx={{ fontSize: 13, color: inkFaint, textAlign: 'center' }}>
                            {t('upl_canvas_hint', 'Label at physical size · drag to move, corner handle to resize, 0.5 mm snap')}
                            {' · '}{t('upl_kbd_hint', 'Del removes · arrows move (0.5 mm) · Shift+arrow resizes')}
                            {' · '}{t('upl_multi_hint', 'Ctrl+click selects multiple')}
                        </Typography>
                    </Box>

                    {/* Properties */}
                    <Box sx={{ borderLeft: { md: '1px solid ' + panelBorder }, p: 1.4 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: inkFaint, mb: 0.8 }}>
                            {selected
                                ? (t('upl_properties', 'PROPERTIES') + ' — ' + selected.type.toUpperCase())
                                : (selectedIds.length > 1
                                    ? selectedIds.length + ' ' + t('upl_selected_count', 'ELEMENTS SELECTED')
                                    : t('upl_no_selection', 'NO SELECTION'))}
                        </Typography>
                        {selectedIds.length > 1 && (
                            <div>
                                <Typography sx={{ fontSize: 13.5, color: inkFaint, mb: 1 }}>
                                    {t('upl_multi_note', 'Drag or use the arrow keys to move the group together.')}
                                </Typography>
                                {/* FastReport Alignment palette: relative to
                                    the FIRST selected element. */}
                                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: inkFaint, mb: 0.4 }}>
                                    {t('upl_ms_align', 'ALIGN')}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                                    <ToggleButton value="l" sx={{ p: 0.6 }} title={t('upl_ms_left', 'Align left edges')}
                                        onClick={() => alignSelection('left')}>
                                        <AlignHorizontalLeftIcon sx={{ fontSize: 17 }} />
                                    </ToggleButton>
                                    <ToggleButton value="hc" sx={{ p: 0.6 }} title={t('upl_ms_hcenter', 'Align horizontal centers')}
                                        onClick={() => alignSelection('hcenter')}>
                                        <AlignHorizontalCenterIcon sx={{ fontSize: 17 }} />
                                    </ToggleButton>
                                    <ToggleButton value="r" sx={{ p: 0.6 }} title={t('upl_ms_right', 'Align right edges')}
                                        onClick={() => alignSelection('right')}>
                                        <AlignHorizontalRightIcon sx={{ fontSize: 17 }} />
                                    </ToggleButton>
                                    <ToggleButton value="w" sx={{ p: 0.6 }} title={t('upl_ms_width', 'Same width')}
                                        onClick={() => alignSelection('width')}>
                                        <HeightIcon sx={{ fontSize: 17, transform: 'rotate(90deg)' }} />
                                    </ToggleButton>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                                    <ToggleButton value="t" sx={{ p: 0.6 }} title={t('upl_ms_top', 'Align top edges')}
                                        onClick={() => alignSelection('top')}>
                                        <AlignVerticalTopIcon sx={{ fontSize: 17 }} />
                                    </ToggleButton>
                                    <ToggleButton value="vc" sx={{ p: 0.6 }} title={t('upl_ms_vcenter', 'Align vertical centers')}
                                        onClick={() => alignSelection('vcenter')}>
                                        <AlignVerticalCenterIcon sx={{ fontSize: 17 }} />
                                    </ToggleButton>
                                    <ToggleButton value="b" sx={{ p: 0.6 }} title={t('upl_ms_bottom', 'Align bottom edges')}
                                        onClick={() => alignSelection('bottom')}>
                                        <AlignVerticalBottomIcon sx={{ fontSize: 17 }} />
                                    </ToggleButton>
                                    <ToggleButton value="h" sx={{ p: 0.6 }} title={t('upl_ms_height', 'Same height')}
                                        onClick={() => alignSelection('height')}>
                                        <HeightIcon sx={{ fontSize: 17 }} />
                                    </ToggleButton>
                                </Box>
                                <Typography sx={{ fontSize: 11.5, color: inkFaint, mb: 1 }}>
                                    {t('upl_ms_anchor_hint', 'Relative to the first-selected element')}
                                </Typography>
                                <Button fullWidth size="small" sx={{ fontSize: 13, color: Colors.def_red, textTransform: 'none' }}
                                    onClick={deleteSelected}>
                                    {t('upl_delete_el', 'Delete element')} ({selectedIds.length})
                                </Button>
                            </div>
                        )}
                        {selected && (
                            <div>
                                {fr3Mode && selected.name && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1, flexWrap: 'wrap' }}>
                                        <Typography sx={{ fontSize: 13.5, fontFamily: 'Consolas, monospace', fontWeight: 700 }}>
                                            {selected.name}
                                        </Typography>
                                        {scriptedNames[selected.name] && (
                                            <Typography sx={{ fontSize: 11.5, px: 0.7, py: 0.1, borderRadius: 1,
                                                backgroundColor: '#FBF0DA', color: '#7A5D00', whiteSpace: 'nowrap' }}>
                                                {t('upl_in_script', 'used by script')}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                                {iconRow(<EastIcon sx={{ fontSize: 18 }} />, 'X (mm)',
                                    numField(selected.x, (v) => editElement(selected.id, { x: v })))}
                                {iconRow(<SouthIcon sx={{ fontSize: 18 }} />, 'Y (mm)',
                                    numField(selected.y, (v) => editElement(selected.id, { y: v })))}
                                {iconRow(<HeightIcon sx={{ fontSize: 18, transform: 'rotate(90deg)' }} />,
                                    t('upl_width_mm', 'Width (mm)'),
                                    numField(selected.w || 0, (v) => editElement(selected.id, { w: v })))}
                                {iconRow(<HeightIcon sx={{ fontSize: 18 }} />,
                                    t('upl_height_mm', 'Height (mm)'),
                                    numField(selected.h || 0, (v) => editElement(selected.id, { h: v })))}

                                {selected.type === 'text' && (
                                    <div>
                                        <TextField
                                            fullWidth size="small" value={selected.text || ''} multiline minRows={3} maxRows={6}
                                            onChange={(e) => editElement(selected.id, { text: e.target.value })}
                                            sx={{ mb: 1 }} inputProps={{ style: { fontSize: 14.5 } }}
                                        />
                                        {!fr3Mode && propRow(t('upl_add_field', 'Field'), (
                                            <Select size="small" value="" displayEmpty sx={{ fontSize: 13.5, minWidth: 112 }}
                                                onChange={(e) => editElement(selected.id, { text: (selected.text || '') + '[' + e.target.value + ']' })}>
                                                <MenuItem value="" disabled sx={{ fontSize: 13.5 }}>{t('upl_insert', 'Insert…')}</MenuItem>
                                                {CLASSIC_TOKENS.map((token) => (
                                                    <MenuItem key={token} value={token} sx={{ fontSize: 13.5 }}>[{token}]</MenuItem>
                                                ))}
                                            </Select>
                                        ))}
                                        {fr3Mode && propRow(t('upl_font_family', 'Font'), (
                                            <Select size="small" value={selected.fontName || 'Arial'}
                                                sx={{ fontSize: 13.5, minWidth: 132 }}
                                                onChange={(e) => editElement(selected.id, { fontName: e.target.value })}>
                                                {(FR3_FONTS.indexOf(selected.fontName || 'Arial') < 0
                                                    ? [selected.fontName, ...FR3_FONTS] : FR3_FONTS).map((f) => (
                                                    <MenuItem key={f} value={f}
                                                        sx={{ fontSize: 13.5, fontFamily: '"' + f + '", Arial, sans-serif' }}>
                                                        {f}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        ))}
                                        {propRow(t('upl_font_dots', 'Font (dots)'), numField(selected.font || 24, (v) => editElement(selected.id, { font: v, fontW: v })))}
                                        {propRow(t('upl_align', 'Align'), (
                                            <ToggleButtonGroup size="small" exclusive value={selected.align || 'L'}
                                                onChange={(e, v) => { if (v) editElement(selected.id, { align: v }); }}>
                                                <ToggleButton value="L" sx={{ p: 0.6 }} title={t('upl_align_left', 'Left')}>
                                                    <FormatAlignLeftIcon sx={{ fontSize: 17 }} />
                                                </ToggleButton>
                                                <ToggleButton value="C" sx={{ p: 0.6 }} title={t('upl_align_center', 'Center')}>
                                                    <FormatAlignCenterIcon sx={{ fontSize: 17 }} />
                                                </ToggleButton>
                                                <ToggleButton value="R" sx={{ p: 0.6 }} title={t('upl_align_right', 'Right')}>
                                                    <FormatAlignRightIcon sx={{ fontSize: 17 }} />
                                                </ToggleButton>
                                                <ToggleButton value="J" sx={{ p: 0.6 }} title={t('upl_align_justify', 'Justify')}>
                                                    <FormatAlignJustifyIcon sx={{ fontSize: 17 }} />
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        ))}
                                        {fr3Mode && propRow(t('upl_style', 'Style'), (
                                            <ToggleButtonGroup size="small"
                                                value={[1, 2, 4].filter((bit) => ((selected.fontStyle || 0) & bit) !== 0)}
                                                onChange={(e, vals) => editElement(selected.id,
                                                    { fontStyle: (vals || []).reduce((mask, bit) => mask | bit, 0) })}>
                                                <ToggleButton value={1} sx={{ p: 0.6 }} title={t('upl_style_bold', 'Bold')}>
                                                    <FormatBoldIcon sx={{ fontSize: 17 }} />
                                                </ToggleButton>
                                                <ToggleButton value={2} sx={{ p: 0.6 }} title={t('upl_style_italic', 'Italic')}>
                                                    <FormatItalicIcon sx={{ fontSize: 17 }} />
                                                </ToggleButton>
                                                <ToggleButton value={4} sx={{ p: 0.6 }} title={t('upl_style_underline', 'Underline')}>
                                                    <FormatUnderlinedIcon sx={{ fontSize: 17 }} />
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        ))}
                                        {fr3Mode && propRow(t('upl_borders', 'Borders'), (
                                            <ToggleButtonGroup size="small"
                                                value={[1, 4, 8, 2].filter((bit) => ((selected.frame || 0) & bit) !== 0)}
                                                onChange={(e, vals) => editElement(selected.id,
                                                    { frame: (vals || []).reduce((mask, bit) => mask | bit, 0) })}>
                                                <ToggleButton value={1} sx={{ p: 0.6 }} title={t('upl_border_left', 'Left border')}>
                                                    <BorderLeftIcon sx={{ fontSize: 17 }} />
                                                </ToggleButton>
                                                <ToggleButton value={4} sx={{ p: 0.6 }} title={t('upl_border_top', 'Top border')}>
                                                    <BorderTopIcon sx={{ fontSize: 17 }} />
                                                </ToggleButton>
                                                <ToggleButton value={8} sx={{ p: 0.6 }} title={t('upl_border_bottom', 'Bottom border')}>
                                                    <BorderBottomIcon sx={{ fontSize: 17 }} />
                                                </ToggleButton>
                                                <ToggleButton value={2} sx={{ p: 0.6 }} title={t('upl_border_right', 'Right border')}>
                                                    <BorderRightIcon sx={{ fontSize: 17 }} />
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        ))}
                                        {!fr3Mode && propRow(t('upl_max_lines', 'Max lines'), numField(selected.maxLines || 1, (v) => editElement(selected.id, { maxLines: Math.max(1, Math.min(9, Math.round(v))) })))}
                                        {!fr3Mode && propRow(t('upl_rotation', 'Rotation'), (
                                            <Select size="small" value={selected.rotation || 'N'} sx={{ fontSize: 13.5 }}
                                                onChange={(e) => editElement(selected.id, { rotation: e.target.value })}>
                                                {[['N', '0°'], ['R', '90°'], ['I', '180°'], ['B', '270°']].map(([v, label]) => (
                                                    <MenuItem key={v} value={v} sx={{ fontSize: 13.5 }}>{label}</MenuItem>
                                                ))}
                                            </Select>
                                        ))}
                                        {!fr3Mode && propRow(t('upl_background', 'White background'), (
                                            <Switch size="small" checked={selected.background === true}
                                                onChange={() => editElement(selected.id, { background: !selected.background })} />
                                        ))}
                                    </div>
                                )}

                                {selected.type === 'barcode' && (
                                    <div>
                                        {!fr3Mode && propRow(t('upl_bar_type', 'Type'), (
                                            <Select size="small" value={selected.barType || 'EAN13'} sx={{ fontSize: 13.5 }}
                                                onChange={(e) => editElement(selected.id, { barType: e.target.value })}>
                                                {BAR_TYPES.map((bt) => (<MenuItem key={bt} value={bt} sx={{ fontSize: 13.5 }}>{bt}</MenuItem>))}
                                            </Select>
                                        ))}
                                        {!fr3Mode && propRow(t('upl_module', 'Module (dots)'), numField(selected.module || 2, (v) => editElement(selected.id, { module: Math.max(1, Math.min(4, Math.round(v))) })))}
                                        {!fr3Mode && propRow(t('upl_interp', 'Digits below'), (
                                            <Switch size="small" checked={selected.interpretation !== false}
                                                onChange={() => editElement(selected.id, { interpretation: !selected.interpretation })} />
                                        ))}
                                        <TextField
                                            fullWidth size="small" value={selected.data || '[BARCODE]'}
                                            onChange={(e) => editElement(selected.id, { data: e.target.value })}
                                            sx={{ mb: 1 }} inputProps={{ style: { fontSize: 14.5 } }}
                                        />
                                        {!fr3Mode && propRow(t('upl_background', 'White background'), (
                                            <Switch size="small" checked={selected.background === true}
                                                onChange={() => editElement(selected.id, { background: !selected.background })} />
                                        ))}
                                    </div>
                                )}

                                {selected.type === 'box' && (
                                    <div>
                                        {!fr3Mode && propRow(t('upl_thickness', 'Thickness (dots)'), numField(selected.thickness || 1, (v) => editElement(selected.id, { thickness: Math.max(1, Math.round(v)) })))}
                                        {!fr3Mode && (((selected.w || 0) === 0 || (selected.h || 0) === 0) ? (
                                            // A line: direction preset swaps the dimensions.
                                            propRow(t('upl_direction', 'Direction'), (
                                                <Select size="small" sx={{ fontSize: 13.5 }}
                                                    value={(selected.h || 0) === 0 ? 'H' : 'V'}
                                                    onChange={(e) => {
                                                        const horizontal = e.target.value === 'H';
                                                        const length = Math.max(selected.w || 0, selected.h || 0);
                                                        editElement(selected.id, {
                                                            w: horizontal ? length : 0,
                                                            h: horizontal ? 0 : length,
                                                        });
                                                    }}>
                                                    <MenuItem value="H" sx={{ fontSize: 13.5 }}>{t('upl_dir_h', 'Horizontal')}</MenuItem>
                                                    <MenuItem value="V" sx={{ fontSize: 13.5 }}>{t('upl_dir_v', 'Vertical')}</MenuItem>
                                                </Select>
                                            ))
                                        ) : (
                                            propRow(t('upl_fill', 'White fill'), (
                                                <Switch size="small" checked={selected.fill === 'white'}
                                                    onChange={() => editElement(selected.id, { fill: selected.fill === 'white' ? 'none' : 'white' })} />
                                            ))
                                        ))}
                                    </div>
                                )}

                                <Button fullWidth size="small" sx={{ mt: 1, fontSize: 13, color: Colors.def_red, textTransform: 'none' }}
                                    onClick={deleteSelected}
                                    disabled={!!(selected.name && scriptedNames[selected.name])}>
                                    {t('upl_delete_el', 'Delete element')}
                                </Button>
                            </div>
                        )}
                        {/* PAGE — the .fr3's own paper size and margins.
                            Editing the size makes the paper custom
                            (PaperSize 256), otherwise the printer driver
                            would override the millimetres at print time. */}
                        {fr3Mode && (
                        <React.Fragment>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: inkFaint, mt: 1.6, mb: 0.4 }}>
                                {t('upl_page', 'PAGE')}
                            </Typography>
                            {iconRow(<HeightIcon sx={{ fontSize: 18, transform: 'rotate(90deg)' }} />,
                                t('upl_page_width', 'Paper width (mm)'),
                                numField(model.widthMm, (v) => applyPageSettings({ widthMm: Math.max(10, v) })))}
                            {iconRow(<HeightIcon sx={{ fontSize: 18 }} />,
                                t('upl_page_height', 'Paper height (mm)'),
                                numField(model.heightMm, (v) => applyPageSettings({ heightMm: Math.max(10, v) })))}
                            {(() => {
                                const page = fr3Ref.current ? getFr3Page(fr3Ref.current.handle) : null;
                                if (!page) return null;
                                return (
                                    <React.Fragment>
                                        {propRow(t('upl_margin_lr', 'Margin L/R'), (
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {numField(page.leftMm, (v) => applyPageSettings({ leftMm: Math.max(0, v) }), 44)}
                                                {numField(page.rightMm, (v) => applyPageSettings({ rightMm: Math.max(0, v) }), 44)}
                                            </Box>
                                        ))}
                                        {propRow(t('upl_margin_tb', 'Margin T/B'), (
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {numField(page.topMm, (v) => applyPageSettings({ topMm: Math.max(0, v) }), 44)}
                                                {numField(page.bottomMm, (v) => applyPageSettings({ bottomMm: Math.max(0, v) }), 44)}
                                            </Box>
                                        ))}
                                    </React.Fragment>
                                );
                            })()}
                            <Typography sx={{ fontSize: 11.5, color: inkFaint, mt: 0.3, lineHeight: 1.35 }}>
                                {t('upl_page_hint', 'Changing the size sets the paper to custom, so the printer keeps these millimetres.')}
                            </Typography>
                        </React.Fragment>
                        )}
                        {!fr3Mode && (
                        <React.Fragment>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: inkFaint, mt: 1.6, mb: 0.4 }}>LABEL</Typography>
                            <Typography sx={{ fontSize: 13.5, fontFamily: 'Consolas, monospace' }}>
                                ^PW {Math.round(model.widthMm * (model.dpmm || 8))} · ^LL {Math.round(model.heightMm * (model.dpmm || 8))}
                            </Typography>
                        </React.Fragment>
                        )}
                    </Box>
                </Box>
            )}

            {/* Compiled ZPL strip (ZPL documents only — fr3 saves the XML itself) */}
            {!fr3Mode && (
            <Box sx={{
                mt: 1, p: 1, borderRadius: 1, border: '1px solid ' + panelBorder,
                fontFamily: 'Consolas, monospace', fontSize: 13, whiteSpace: 'pre',
                overflowX: 'auto', maxHeight: 140, overflowY: 'auto',
                backgroundColor: isDark ? Colors.dark_accent_darkest : '#F4F6F8',
                color: isDark ? Colors.dark_text_accent : '#51616E',
            }}>
                {zplOut}
            </Box>
            )}

            <Dialog open={renderOpen} onClose={() => setRenderOpen(false)} maxWidth="md">
                <DialogTitle sx={{ fontSize: 15 }}>{t('upl_exact_render', 'Exact render (Labelary)')}</DialogTitle>
                <DialogContent>
                    {renderImg !== '' && <img src={renderImg} alt="Label render" style={{ maxWidth: '100%', border: '1px solid #C9D2DA' }} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRenderOpen(false)} sx={{ textTransform: 'none' }}>{t('close', 'Close')}</Button>
                </DialogActions>
            </Dialog>

            {/* Report script (PascalScript). Object names in the properties
                panel match the names used here, so a rule can be traced to
                the object it formats. Saved with the layout, not separately. */}
            <Dialog open={scriptOpen} onClose={() => setScriptOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontSize: 16.5 }}>
                    {t('upl_script_title', 'Report script (PascalScript)')}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 12.5, color: inkFaint, mb: 1 }}>
                        {t('upl_script_hint', 'Objects are addressed by the names shown in the properties panel. Changes are written when you save the layout.')}
                    </Typography>
                    <TextField
                        fullWidth multiline minRows={16} maxRows={26}
                        value={scriptText}
                        onChange={(e) => setScriptText(e.target.value)}
                        inputProps={{ style: { fontFamily: 'Consolas, monospace', fontSize: 13, whiteSpace: 'pre' } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setScriptOpen(false)} sx={{ textTransform: 'none' }}>
                        {t('cancel', 'Cancel')}
                    </Button>
                    <Button variant="contained" sx={{ textTransform: 'none', backgroundColor: Colors.sft_blue }}
                        onClick={() => {
                            if (fr3Ref.current) setFr3Script(fr3Ref.current.handle, scriptText);
                            setScriptOpen(false);
                            notify(t('upl_script_applied', 'Script updated — save the layout to write it'), true);
                        }}>
                        {t('upl_apply', 'Apply')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default LabelDesignerScreen;
